import {
	AssetSchema,
	AssetSchemaFilter,
	AssetSchemaRaw,
} from '../types/asset.types';
import {
	fromRawSystem,
	getSystemTypeByAssetTypeRaw,
	getSystemTypeRaw,
	getSystemTypesRaw,
} from '../system';
import {
	Capabilities as SystemCapabilities,
	Capability as SystemCapability,
} from '../types/capabilities/capabilities.system.types';
import {
	Capabilities as AssetCapabilities,
	Capability as AssetCapability,
} from '../types/capabilities/capabilities.asset.types';

import { AssetSchemaImpl } from './asset.class';
import { SystemTypeRaw } from '../types/system.types';
import { loadAssetFile } from './file';

/**
 * Converts raw asset schema and system type to an AssetSchema instance.
 * @internal
 * @param rawSystemType - The raw system type.
 * @param rawAssetSchema - The raw asset schema.
 * @returns The AssetSchema instance.
 */
export const fromRawAssetSchema = (
	rawSystemType: SystemTypeRaw,
	rawAssetSchema: AssetSchemaRaw,
): AssetSchema => {
	return new AssetSchemaImpl({
		...rawAssetSchema,
		SystemType: fromRawSystem(rawSystemType),
	});
};

/**
 * Retrieves raw asset schemas based on the provided filter.
 * @param filter - The filter to apply when retrieving asset schemas.
 * @returns A promise that resolves to an array of tuples containing system type and asset schema.
 */
export const getAssetSchemasRaw = async (
	filter?: AssetSchemaFilter,
): Promise<[SystemTypeRaw, AssetSchemaRaw][]> => {
	const systemTypes = await getSystemTypesRaw();
	const allAssetSchemas = [];
	if (!filter) {
		for (const systemType of systemTypes) {
			for (const assetType of systemType.AssetTypes) {
				const schema = await getAssetSchemaRaw({
					systemTypeId: systemType.SystemType,
					assetTypeId: assetType,
				});
				if (schema) {
					allAssetSchemas.push(schema);
				}
			}
		}
	} else if ('capability' in filter) {
		const isSystemCapability = SystemCapabilities.includes(
			filter.capability as SystemCapability,
		);
		const isAssetCapability = AssetCapabilities.includes(
			filter.capability as AssetCapability,
		);
		if (isSystemCapability || isAssetCapability) {
			for (const systemType of systemTypes) {
				const loadedSystemType = fromRawSystem(systemType);
				if (!isAssetCapability) {
					if (
						!loadedSystemType.hasCapability(
							filter.capability as SystemCapability,
						)
					) {
						continue;
					} else {
						for (const assetTypeId of systemType.AssetTypes) {
							const schema = await getAssetSchemaRaw({
								systemTypeId: systemType.SystemType,
								assetTypeId: assetTypeId,
							});
							if (schema == undefined) {
								continue;
							}
							allAssetSchemas.push(schema);
						}
					}
				} else {
					// In case of overlapping capabilities, asset takes precedence
					// to keep the api contract in tact
					for (const assetTypeId of systemType.AssetTypes) {
						const schema = await getAssetSchemaRaw({
							systemTypeId: systemType.SystemType,
							assetTypeId: assetTypeId,
						});
						if (schema == undefined) {
							continue;
						}
						const [_, assetSchema] = schema;
						if (
							fromRawAssetSchema(
								systemType,
								assetSchema,
							)?.hasCapability(
								filter.capability as AssetCapability,
							)
						) {
							allAssetSchemas.push(schema);
						}
					}
				}
			}
		}
	} else if ('systemTypeId' in filter) {
		const systemType = systemTypes.find(
			s => s.SystemType === filter.systemTypeId,
		);

		if (systemType) {
			for (const assetTypeId of systemType.AssetTypes) {
				const schema = await getAssetSchemaRaw({
					systemTypeId: systemType.SystemType,
					assetTypeId: assetTypeId,
				});
				if (schema) {
					allAssetSchemas.push(schema);
				}
			}
		}
	} else if ('inspectionType' in filter) {
		for (const systemType of systemTypes) {
			for (const assetTypeId of systemType.AssetTypes) {
				const schema = await getAssetSchemaRaw({
					systemTypeId: systemType.SystemType,
					assetTypeId: assetTypeId,
				});
				if (schema == undefined) {
					continue;
				}
				const [_, assetSchema] = schema;
				if (
					fromRawAssetSchema(
						systemType,
						assetSchema,
					)?.hasInspectionType(filter.inspectionType)
				) {
					allAssetSchemas.push(schema);
				}
			}
		}
	}

	return allAssetSchemas;
};

/**
 * Retrieves asset schemas based on the provided filter.
 * @param filter - The filter to apply when retrieving asset schemas.
 * @returns A promise that resolves to an array of AssetSchema instances.
 */
export const getAssetSchemas = async (
	filter?: AssetSchemaFilter,
): Promise<AssetSchema[]> => {
	return (await getAssetSchemasRaw(filter)).map(([systemType, assetType]) =>
		fromRawAssetSchema(systemType, assetType),
	);
};

/**
 * Parameters for retrieving a raw asset schema.
 */
export interface GetAssetSchemaParams {
	systemTypeId?: string;
	assetTypeId: string;
}

/**
 * Retrieves a raw asset schema based on the provided parameters.
 * @param getAssetSchemaParams - The parameters for retrieving the asset schema.
 * @returns A promise that resolves to a tuple containing system type and asset schema, or undefined if not found.
 */
export const getAssetSchemaRaw = async (
	getAssetSchemaParams: GetAssetSchemaParams,
): Promise<[SystemTypeRaw, AssetSchemaRaw] | undefined> => {
	let systemType;
	if (getAssetSchemaParams.systemTypeId) {
		systemType = await getSystemTypeRaw(getAssetSchemaParams.systemTypeId);
	} else {
		systemType = await getSystemTypeByAssetTypeRaw(
			getAssetSchemaParams.assetTypeId,
		);
	}

	if (!systemType) {
		return undefined;
	}

	const rawAssetSchema = await loadAssetFile(
		systemType.SystemType,
		getAssetSchemaParams.assetTypeId,
	);

	if (!rawAssetSchema) {
		return undefined;
	}

	return [systemType, rawAssetSchema];
};

/**
 * Retrieves an asset schema based on the provided parameters.
 * @param getAssetSchemaParams - The parameters for retrieving the asset schema.
 * @returns A promise that resolves to an AssetSchema instance, or undefined if not found.
 */
export const getAssetSchema = async (
	getAssetSchemaParams: GetAssetSchemaParams,
): Promise<AssetSchema | undefined> => {
	const maybeSchema = await getAssetSchemaRaw(getAssetSchemaParams);

	if (!maybeSchema) {
		return undefined;
	}

	return fromRawAssetSchema(maybeSchema[0], maybeSchema[1]);
};
